import { useCallback } from "react";
import type { ReactElement } from "react";
import type { Artikel, Unternehmen } from "@kundeninfo/api-client";
import { useSofortPopup } from "@kundeninfo/ui";
import { Chip, Knopf, Objekt, Verweis } from "@kundeninfo/ui/me-design";
import { useKennungsAttribut } from "../../components/objektPanel/kennungsAttribut.js";
import { firstParagraph, formatBetrag } from "../../format.js";
import { renderMarkdown } from "../../markdown.js";
import { ARTICLE_TEXT as T, perUnit, usage } from "./articleTexts.js";

export type ArtikelKachelProps = {
  artikel: Artikel;
  /** Das Unternehmen des Artikels (Kennzeichen-Chip in der Hausfarbe); unbekannt → kein Chip. */
  unternehmen: Unternehmen | undefined;
  /** Stift und ⋯ nur für ADMIN (789) — auch an Kacheln zugeschalteter Unternehmen (788). */
  istAdmin: boolean;
  /** Die Kachel aus der Adresse `/artikel/:id` (793). */
  markiert: boolean;
  onBearbeiten: () => void;
  /** Das ⋯ (Stilllegen/Wieder aktivieren hängt Aufgabe 7 an); `anker` ist der Knopf, an dem das Menü steht. */
  onMehr: (anker: HTMLElement) => void;
};

/** „Konto fehlt“ gilt nur für aktive Artikel — ein stillgelegter kommt in keine neue Position (786, Statusrang 0). */
export const kontoFehlt = (a: Artikel): boolean => a.active && a.bookingAccountId === null;

/**
 * Die ARTIKELKACHEL nach Variante A (Entscheid 802): `Objekt fassung="kachel"` ohne Reiter, ohne Tafeln, ohne etwas zum
 * Umschalten. Kopf: Name, Artikelnummer und rechts Preis mit Einheit (`wert`/`zeit`, 797 mit Nachtrag — Schrift und
 * Gewicht des Namens setzt die Bereichs-CSS über `::part`, nie ein Stil am Aufrufort). Status nur „Konto fehlt“ (Tipp
 * nach 786); „stillgelegt“ ist ein Kennzeichen neben dem Unternehmens-Chip. Inhalt: der erste Absatz der Beschreibung
 * (724) als Markdown, auf vier Zeilen gekürzt, der Volltext im Tipp. Fuß links unten (109): Verweis mit Panelknopf
 * (Stift), dann ⋯; Angaben: die Verwendung (794). Steuer, Konto, Kostenstelle, Quelle, Takt und Laufzeit stehen nur im
 * Dialog.
 *
 * Objektart `position` ist der Übergang bis W-18 (`artikel`, 720; Erinnerung in docs/ME-DESIGN.md).
 *
 * **Die Zelle.** Das `Objekt` trägt weder Klasse noch Stil (Sperrtest „Anatomie der Objekte“). Was der Bereich an der
 * Kachel braucht — Container für die Preiszeile (`cqi`, 300 px nach Ruling 811), Abblenden der stillgelegten, Rand der markierten,
 * Kennung für den Sprung — hängt an der Rasterzelle `bart__zelle` um das Objekt.
 */
export function ArtikelKachel(p: ArtikelKachelProps): ReactElement {
  const a = p.artikel;
  const warnung = kontoFehlt(a);
  const kontoTipp = useSofortPopup<HTMLElement>(warnung ? T.missingAccountTip : "");
  const merke = useKennungsAttribut("data-artikel-id", a.id);
  /* Der Tipp zu „Konto fehlt“ hängt am Status selbst (Teil `status` im Schatten von me-objekt), nicht an der ganzen
     Kachel — sonst stünde er über Name, Preis und Kurztext. Ohne Warnung ist er stumm. */
  const statusTeil = useCallback((el: HTMLElement | null) => {
    const status = el?.shadowRoot?.querySelector<HTMLElement>("[part~='status']") ?? null;
    kontoTipp(status);
  }, [kontoTipp]);
  const kurz = firstParagraph(a.description);
  const volltext = useSofortPopup<HTMLDivElement>(a.description?.trim() ?? "");
  const hatPreis = Number.isFinite(a.netUnitPrice);
  const klassen = ["bart__zelle", a.active ? "" : "bart__zelle--still", p.markiert ? "bart__zelle--markiert" : ""]
    .filter((k) => k !== "").join(" ");
  // Der Kurztext auf vier festen Zeilen (Bereichs-CSS), der Volltext im Tipp; ohne Text leise „Kein Text hinterlegt“.
  const inhalt = kurz === null
    ? <p className="bart__kurz bart__kurz--leer">{T.noText}</p>
    : <div ref={volltext} className="bart__kurz">{renderMarkdown(kurz)}</div>;
  return (
    <div ref={merke} className={klassen}>
      <Objekt ref={statusTeil} fassung="kachel" art="position" name={a.name} kennung={a.productNumber}
        wert={hatPreis ? formatBetrag(a.netUnitPrice, "EUR") : T.noPrice}
        {...(hatPreis ? { zeit: perUnit(a.unit) } : {})}
        {...(warnung ? { status: T.missingAccount, statusArt: "warnung" as const } : {})}
        kennzeichen={<>
          {p.unternehmen !== undefined && <Chip farbe={p.unternehmen.colorHex}>{p.unternehmen.shortCode}</Chip>}
          {!a.active && <Chip statusArt="aus">{T.inactive}</Chip>}
        </>}
        inhalt={inhalt}
        {...(p.istAdmin ? { fuss: <>
          <Verweis panel={T.editArticle(a.productNumber)} onPanel={p.onBearbeiten} />
          <Knopf art="leise" kompakt aria-label={T.moreFor(a.productNumber)}
            onClick={(e) => p.onMehr(e.currentTarget as HTMLElement)}>{T.more}</Knopf>
        </> } : {})}
        angaben={usage(a.usage.offers, a.usage.orders)} />
    </div>
  );
}
